import { AUTH_PATHS, ROUTE_PATHS } from "@client-shared/lib/constants";
import * as api from "@client-shared/services/api";
import { vendorSchema } from "@shared/schemas";
import type { AccessibleModule, FeatureName, PagePermission, Vendor } from "@shared/types";
import { TFunction } from "i18next";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { handleDisplayErrorData } from "../../services/api/utilities/handleDisplayErrorData";
import { vendorLocalStorageKeys } from "../vendor/types";
import {
  clearRedirectUrl,
  handleLocalStorage,
  initialState,
  reducer,
  setAuthLoading,
  setContactId,
  setInitialized,
  setIsRoasterAdmin,
  setProfileName,
  setRedirectUrl,
  setRole,
  setSFDCAuthTokens,
} from "./actions";
import { getRedirectTarget } from "./crossAppRedirect";
import useSfdcAuthInterceptor from "./SFDCAuthInterceptor";
import { TokenRefreshCancelledError } from "./token-refresh-cancelled-error";
import { HandleSetVendor, SFDCAuthActions, SFDCAuthContextState, sfdcLocalStorageKeys } from "./types";

const TOKEN_REFRESH_MAX_HOURS = 4;

export const TOKEN_REFRESH_LONG_TIMEOUT = 1000 * 60 * 60 * TOKEN_REFRESH_MAX_HOURS; // milliseconds * seconds * minutes * hours

export const AUTH_ERROR_LOGOUT_PATH = `${ROUTE_PATHS.LOGOUT}?logout-type=error`;
export const AUTH_NO_PREVIOUS_SESSION_LOGOUT_PATH = `${ROUTE_PATHS.LOGOUT}?logout-type=no-data`;

const BREVILLE_ROASTER_ADMIN_PROFILE_NAME = "Breville Roaster Admin Profile";

const defaultContextState: SFDCAuthContextState = [initialState, () => undefined];

export const SFDCAuthContext = React.createContext(defaultContextState);

type Props = {
  children: React.ReactNode;
};

export const SfdcAuthProvider: React.FC<Props> = ({ children }: Props) => {
  // get the current route
  const isAuthRoute = useIsGetAuthRoute();
  const [authState, authDispatch] = React.useReducer(reducer, initialState);
  const [isLogoutInitiated, setIsLogoutInitiated] = React.useState(false);
  const isLogoutInitiatedRef = React.useRef(false);
  const navigate = useNavigate();
  const tokenRefreshShortTimeOut = React.useRef<NodeJS.Timeout | null>(null);
  const isTokenFetchInitiatedRef = React.useRef<boolean>(false);
  const { i18n } = useTranslation();
  const translations = React.useMemo(() => i18n.t, [i18n.t]);

  // set
  const setResponseInterceptorRef = React.useRef<number | null>(null);
  const setRequestInterceptorRef = React.useRef<number | null>(null);

  // --------------------------------------------------------------------------------------------
  // methods ----------------------------------//
  // --------------------------------------------------------------------------------------------

  const navigateToLogoutWithError = React.useCallback(() => {
    navigate(AUTH_ERROR_LOGOUT_PATH);
  }, [navigate]);

  const navigateToLogout = React.useCallback(() => {
    navigate(ROUTE_PATHS.LOGOUT);
  }, [navigate]);

  const handleSetRequestInterceptor = React.useCallback(
    () => (set: () => number) => {
      setRequestInterceptorRef.current = set();
    },
    [setRequestInterceptorRef]
  );

  const handleSetResponseInterceptor = React.useCallback(
    (handleError: () => void, refreshTokenIfExpired: (token: string) => Promise<void>) =>
      (set: (handleError: () => void, refreshTokenIfExpired: (token: string) => Promise<void>) => number) => {
        setResponseInterceptorRef.current = set(handleError, refreshTokenIfExpired);
      },
    [setResponseInterceptorRef]
  );

  // --------------------------------------------------------------------------------------------
  // refresh the token
  const runTokenRefresh = React.useCallback(
    async (refreshToken: string | null | undefined) => {
      const storedIsRoasterAdmin = localStorage.getItem(sfdcLocalStorageKeys.sfdcIsRoasterAdmin) === "true";
      await refreshTokens(
        refreshToken,
        Boolean(storedIsRoasterAdmin || authState.isRoasterAdmin),
        authDispatch,
        navigate,
        () => !isLogoutInitiatedRef.current
      );
    },
    [authDispatch, authState.isRoasterAdmin, navigate]
  );

  // Background refreshes have no caller to notify, so a refresh cancelled by logout is simply dropped.
  const handleRefreshToken = React.useCallback(
    async (refreshToken: string | null | undefined) => {
      try {
        await runTokenRefresh(refreshToken);
      } catch (error) {
        if (!(error instanceof TokenRefreshCancelledError)) throw error;
      }
    },
    [runTokenRefresh]
  );

  // The interceptor must see the cancellation so it rejects the queued requests instead of retrying them.
  const refreshTokenIfExpired = React.useCallback(
    async (refreshToken: string) => {
      await runTokenRefresh(refreshToken);
    },
    [runTokenRefresh]
  );

  // --------------------------------------------------------------------------------------------
  // setInterceptors

  const { handleSetAllInterceptors, handleEjectAllInterceptors } = useSfdcAuthInterceptor();

  const handleSetInterceptors = React.useCallback(() => {
    if (setRequestInterceptorRef.current || setResponseInterceptorRef.current) {
      handleEjectAllInterceptors(setRequestInterceptorRef.current, setResponseInterceptorRef.current);
    }

    handleSetAllInterceptors(handleSetRequestInterceptor(), handleSetResponseInterceptor(navigateToLogout, refreshTokenIfExpired));
  }, [
    handleSetAllInterceptors,
    handleSetRequestInterceptor,
    handleSetResponseInterceptor,
    navigateToLogout,
    handleEjectAllInterceptors,
    refreshTokenIfExpired,
  ]);

  const handleSetInterceptorsFromResponse = React.useCallback(
    (tokenResponse: api.TokenResponse) => {
      const isRoasterAdmin = isBrevilleRoasterAdmin(tokenResponse?.profileName);
      if (!tokenResponse?.idToken) {
        navigateToLogoutWithError();
        return false;
      }
      if (!isRoasterAdmin && !tokenResponse?.vendor?.vendorKey) {
        navigateToLogoutWithError();
        return false;
      }
      handleSetInterceptors();
      return true;
    },
    [handleSetInterceptors, navigateToLogoutWithError]
  );

  const handleAuthErrorLogout = React.useCallback(() => {
    authDispatch(setRole(null));
    authDispatch(setProfileName(null));
    authDispatch(setContactId(null));
    authDispatch(setSFDCAuthTokens(null, null, null));
    authDispatch(setIsRoasterAdmin(false));
    authDispatch(setInitialized());
    navigate(AUTH_ERROR_LOGOUT_PATH);
  }, [authDispatch, navigate]);

  // --------------------------------------------------------------------------------------------
  // fetch new tokens from the code
  const handleFetchNewTokens = React.useCallback(
    async (code: string) => {
      const sfdcAuthTokensResponse = await fetchTokens(code, handleAuthErrorLogout, translations);
      if (!sfdcAuthTokensResponse || isLogoutInitiatedRef.current) {
        return;
      }

      authDispatch(
        setSFDCAuthTokens(sfdcAuthTokensResponse?.accessToken, sfdcAuthTokensResponse?.idToken, sfdcAuthTokensResponse?.refreshToken)
      );
      authDispatch(setIsRoasterAdmin(Boolean(sfdcAuthTokensResponse.isRoasterAdmin)));

      const isValidTokenResponse = handleSetInterceptorsFromResponse(sfdcAuthTokensResponse);
      if (!isValidTokenResponse) {
        return;
      }

      authDispatch(setRole(sfdcAuthTokensResponse?.role ?? null));
      authDispatch(setProfileName(sfdcAuthTokensResponse?.profileName ?? null));
      authDispatch(setContactId(sfdcAuthTokensResponse?.contactId ?? null));
      if (!sfdcAuthTokensResponse.isRoasterAdmin && !sfdcAuthTokensResponse.vendor?.vendorKey) {
        return undefined;
      }
      return {
        vendor: sfdcAuthTokensResponse.vendor,
        isBccUser: sfdcAuthTokensResponse.isBccUser,
        isRoasterAdmin: sfdcAuthTokensResponse.isRoasterAdmin,
      };
    },
    [authDispatch, handleAuthErrorLogout, handleSetInterceptorsFromResponse, translations]
  );

  // --------------------------------------------------------------------------------------------
  // redirect to the correct app based on the user's role
  const handleCrossAppRedirect = React.useCallback((isBccUser: boolean): boolean => {
    const redirectTarget = getRedirectTarget(isBccUser, globalThis.location.hostname);
    if (redirectTarget === null) {
      return false;
    }
    globalThis.location.replace(redirectTarget);
    return true;
  }, []);

  // --------------------------------------------------------------------------------------------
  // fetch the auth token from the code
  const handleFetchTokensOnAuth = React.useCallback(
    async (code: string, handleSetVendor: HandleSetVendor) => {
      if (authState.loading || isLogoutInitiatedRef.current) {
        return;
      }

      authDispatch(setAuthLoading(true));
      try {
        if (authState.sfdcAuthToken && authState.sfdcIdToken && authState.sfdcRefreshToken && authState.initialized) {
          await handleRefreshToken(authState.sfdcRefreshToken);
          if (isLogoutInitiatedRef.current) return;
          const hasSelectedVendor = hasStoredVendorSelection();
          navigate(getPostAuthRedirectPath(Boolean(authState.isRoasterAdmin), authState.redirectUrl, hasSelectedVendor));
          return;
        }

        const sfdcAuthTokensResponse = await handleFetchNewTokens(code);
        if (!sfdcAuthTokensResponse) {
          return;
        }
        const { vendor, isBccUser } = sfdcAuthTokensResponse;
        const isRoasterAdmin = Boolean(sfdcAuthTokensResponse.isRoasterAdmin);
        if (!isRoasterAdmin && handleCrossAppRedirect(isBccUser)) {
          return;
        }
        if (isRoasterAdmin) {
          handleSetVendor(undefined);
        } else {
          handleSetVendor(vendor);
        }

        authDispatch(setInitialized());
        const hasSelectedVendor = !isRoasterAdmin && Boolean(vendor?.vendorKey);
        navigate(getPostAuthRedirectPath(isRoasterAdmin, authState.redirectUrl, hasSelectedVendor));
      } finally {
        authDispatch(setAuthLoading(false));
      }
    },
    [
      authDispatch,
      authState.initialized,
      authState.isRoasterAdmin,
      authState.loading,
      authState.redirectUrl,
      authState.sfdcAuthToken,
      authState.sfdcIdToken,
      authState.sfdcRefreshToken,
      handleFetchNewTokens,
      handleRefreshToken,
      navigate,
      handleCrossAppRedirect,
    ]
  );

  // --------------------------------------------------------------------------------------------
  // get the auth tokens from local storage
  const getAuthTokensAndVendorFromLocalStorage = React.useCallback(() => {
    const vendorString = localStorage.getItem(vendorLocalStorageKeys.vendor);
    const vendor: Vendor | undefined = vendorString ? JSON.parse(vendorString) : undefined;
    return {
      sfdcAuthToken: localStorage.getItem(sfdcLocalStorageKeys.sfdcAuthToken),
      sfdcIdToken: localStorage.getItem(sfdcLocalStorageKeys.sfdcIdToken),
      refreshToken: localStorage.getItem(sfdcLocalStorageKeys.sfdcRefreshToken),
      role: localStorage.getItem(sfdcLocalStorageKeys.role),
      profileName: localStorage.getItem(sfdcLocalStorageKeys.profileName),
      contactId: localStorage.getItem(sfdcLocalStorageKeys.contactId),
      vendor: vendor,
      isRoasterAdmin: localStorage.getItem(sfdcLocalStorageKeys.sfdcIsRoasterAdmin) === "true",
    };
  }, []);

  // --------------------------------------------------------------------------------------------
  // debounce the token refresh
  const handleDebouncedRefreshTokensLong = React.useCallback(() => {
    if (!authState.initialized || !authState.sfdcRefreshToken) {
      return;
    }
    if (tokenRefreshShortTimeOut.current) {
      clearTimeout(tokenRefreshShortTimeOut.current);
    }
    tokenRefreshShortTimeOut.current = setTimeout(() => {
      void handleRefreshToken(authState.sfdcRefreshToken);
    }, TOKEN_REFRESH_LONG_TIMEOUT);
  }, [authState.initialized, authState.sfdcRefreshToken, handleRefreshToken]);

  const getRedirectUrlFromLocalStorage = React.useCallback(() => localStorage.getItem("redirectUrl"), []);

  // --------------------------------------------------------------------------------------------
  // When logout is called we clear the auth provider and the interceptor
  const handleClearAuthProvider = React.useCallback(() => {
    if (isLogoutInitiatedRef.current) return;
    isLogoutInitiatedRef.current = true;
    setIsLogoutInitiated(true);
    authDispatch(setSFDCAuthTokens(null, null, null));
    authDispatch(setIsRoasterAdmin(false));
    authDispatch(setRole(null));
    authDispatch(setProfileName(null));
    authDispatch(setContactId(null));
    authDispatch(clearRedirectUrl());
  }, [authDispatch]);

  // --------------------------------------------------------------------------------------------
  // Auth provider side effects
  // --------------------------------------------------------------------------------------------

  // --------------------------------------------------------------------------------------------
  // set the redirect url in local storage
  React.useEffect(() => {
    if (!authState.initialized || isLogoutInitiated) {
      return;
    }
    if (authState.redirectUrl) {
      return;
    }
    const redirectUrl = getRedirectUrlFromLocalStorage();
    if (redirectUrl) {
      authDispatch(setRedirectUrl(redirectUrl));
    }
  }, [authState.initialized, getRedirectUrlFromLocalStorage, authState.redirectUrl, isLogoutInitiated]);

  // --------------------------------------------------------------------------------------------
  // set the auth tokens from after tokens are set in state
  React.useEffect(() => {
    // if we are on an auth route then initialize the provider and stop
    if (isAuthRoute && !authState.initialized) {
      return authDispatch(setInitialized());
    }

    // Skip if already initiated in this session
    if (isTokenFetchInitiatedRef.current) {
      return;
    }

    if (isAuthRoute && authState.initialized) {
      return;
    }
    if (authState.initialized || isLogoutInitiated) {
      return;
    }
    if (authState.sfdcRefreshToken && authState.sfdcAuthToken && authState.sfdcIdToken) {
      return;
    }

    // Mark as initiated before fetching
    isTokenFetchInitiatedRef.current = true;

    // get the tokens from local storage
    const { sfdcAuthToken, sfdcIdToken, refreshToken, role, profileName, contactId, vendor, isRoasterAdmin } =
      getAuthTokensAndVendorFromLocalStorage();
    // for older sessions when no refresh token was saved we login again
    if (sfdcIdToken && sfdcAuthToken && !refreshToken) {
      return navigate(ROUTE_PATHS.LOGIN);
    }
    // if tokens or vendors are missing then redirect to login page
    if (!sfdcAuthToken || !sfdcIdToken || !refreshToken || (!isRoasterAdmin && !vendor?.vendorKey)) {
      return navigate(ROUTE_PATHS.LOGIN);
    }
    authDispatch(setSFDCAuthTokens(sfdcAuthToken, sfdcIdToken, refreshToken));
    authDispatch(setIsRoasterAdmin(Boolean(isRoasterAdmin)));
    authDispatch(setRole(role ?? null));
    authDispatch(setProfileName(profileName ?? null));
    authDispatch(setContactId(contactId ?? null));
    authDispatch(setInitialized());
    handleSetInterceptors();
  }, [
    authState.initialized,
    authState.sfdcRefreshToken,
    authState.sfdcAuthToken,
    authState.sfdcIdToken,
    getAuthTokensAndVendorFromLocalStorage,
    handleDebouncedRefreshTokensLong,
    navigate,
    authState.redirectUrl,
    isAuthRoute,
    isLogoutInitiated,
    authState,
    handleSetInterceptors,
  ]);

  // --------------------------------------------------------------------------------------------
  // once the tokens are set set the debounce in the background
  React.useEffect(() => {
    // Only set up refresh if we have both initialization and a refresh token
    if (!authState.initialized || !authState.sfdcRefreshToken) {
      return;
    }
    handleDebouncedRefreshTokensLong();
  }, [authState.initialized, authState.sfdcRefreshToken, handleDebouncedRefreshTokensLong, handleRefreshToken]);

  // --------------------------------------------------------------------------------------------
  // what is available to the provider's children
  // --------------------------------------------------------------------------------------------

  // context value
  const contextValue: SFDCAuthContextState = React.useMemo(
    () => [
      authState,
      authDispatch,
      {
        fetchTokensOnAuth: handleFetchTokensOnAuth,
        clearAuthProvider: handleClearAuthProvider,
      },
    ],
    [authState, handleFetchTokensOnAuth, handleClearAuthProvider, authDispatch]
  );

  // --------------------------------------------------------------------------------------------
  // if auth state is initialized then provide the context
  // otherwise return the AppContainerNoAuth with loading
  return <>{authState.initialized ? <SFDCAuthContext.Provider value={contextValue}>{children}</SFDCAuthContext.Provider> : null}</>;
};

// ====================================================================================================================
// side effects ----------------------------------//
// ====================================================================================================================

type SfdcAuthTokenResponse = api.TokenResponse & {
  isRoasterAdmin: boolean;
};

const isBrevilleRoasterAdmin = (profileName?: string) => {
  return profileName?.trim() === BREVILLE_ROASTER_ADMIN_PROFILE_NAME;
};

const hasStoredVendorSelection = () => {
  try {
    const vendorString = localStorage.getItem(vendorLocalStorageKeys.vendor);
    const vendor: Vendor | undefined = vendorString ? JSON.parse(vendorString) : undefined;
    return Boolean(vendor?.vendorKey);
  } catch {
    return false;
  }
};

const getPostAuthRedirectPath = (isRoasterAdmin: boolean, redirectUrl?: string | null, hasSelectedVendor = false) => {
  if (isRoasterAdmin && !hasSelectedVendor) {
    return ROUTE_PATHS.ADMIN_ROASTERS;
  }

  return redirectUrl ?? ROUTE_PATHS.HOME;
};

export const getFeatureNameFromAccountType = (accountType?: string): FeatureName | undefined => {
  if (!accountType) {
    return undefined;
  }

  switch (accountType.trim().toLowerCase()) {
    case "blp":
      return "BLP";
    case "ss":
    case "shipstation":
      return "SS";
    case "non_blp":
    case "bc og":
    case "bc-og":
    case "bc_og":
    case "beanz connect":
    case "beanz-connect":
      return "BC OG";
    default:
      return undefined;
  }
};

interface ParsedAccessibleFeatures {
  pages: PagePermission[];
  menuGroups: AccessibleModule[];
}

const parseAccessibleFeatures = (accessibleFeatures: string | undefined): ParsedAccessibleFeatures => {
  if (!accessibleFeatures) {
    return { pages: [], menuGroups: [] };
  }
  try {
    const parsed = JSON.parse(accessibleFeatures) as Partial<ParsedAccessibleFeatures>;
    return {
      pages: parsed.pages ?? [],
      menuGroups: parsed.menuGroups ?? [],
    };
  } catch {
    console.error("Failed to parse accessibleFeatures", accessibleFeatures);
    return { pages: [], menuGroups: [] };
  }
};

const mergeRbacIntoVendor = (tokenResponse: api.TokenResponse): Vendor | undefined => {
  if (!tokenResponse.vendor) {
    return undefined;
  }

  const featureName = getFeatureNameFromAccountType(tokenResponse.accountType);
  const { pages, menuGroups } = parseAccessibleFeatures(tokenResponse.accessibleFeatures);
  const permissions = {
    pages,
    menuGroups,
  };

  return {
    ...tokenResponse.vendor,
    properties: {
      ...tokenResponse.vendor.properties,
      ...(featureName ? { featureName } : {}),
      permissions,
    },
  };
};

export const fetchTokens = async (
  code: string,
  logOut: () => void,
  translations: TFunction<"translation", undefined>
): Promise<SfdcAuthTokenResponse | undefined> => {
  try {
    const tokenResponse = await api.getOAuthToken(code);
    const isRoasterAdmin = isBrevilleRoasterAdmin(tokenResponse.profileName);
    if (isRoasterAdmin) {
      return { ...tokenResponse, vendor: undefined, isRoasterAdmin: true };
    }

    const vendorWithRbac = mergeRbacIntoVendor(tokenResponse);

    if (vendorWithRbac) {
      const vendorValidation = await vendorSchema.safeParseAsync(vendorWithRbac);
      if (!vendorValidation.success) {
        console.error("Error retrieving Vendor details", vendorValidation.error);
        handleDisplayErrorData(
          translations("apiErrorMessages.getVendorDetails.message"),
          translations("apiErrorMessages.getVendorDetails.title"),
          undefined,
          logOut,
          2000
        );
        return undefined;
      }
    }

    return { ...tokenResponse, vendor: vendorWithRbac, isRoasterAdmin: false };
  } catch (error) {
    console.error("Error retrieving OAuth token", error);
    logOut();
  }
};

export const refreshTokens = async (
  refreshToken: string | undefined | null,
  isRoasterAdmin: boolean,
  authDispatch: (action: SFDCAuthActions) => void,
  navigate: (path: string) => void,
  shouldApplyResponse: () => boolean = () => true
) => {
  if (!refreshToken) {
    console.error("No refresh token found. Logging out...");
    return navigate(ROUTE_PATHS.LOGOUT);
  }

  try {
    const tokenResponse = await api.refreshToken(refreshToken);
    if (!shouldApplyResponse()) throw new TokenRefreshCancelledError();
    const isInvalidTokenResponse = !tokenResponse?.idToken || (!isRoasterAdmin && !tokenResponse?.vendor?.vendorKey);

    if (isInvalidTokenResponse) {
      navigate(ROUTE_PATHS.LOGOUT);
      return;
    }

    handleLocalStorage(sfdcLocalStorageKeys.sfdcIdToken, tokenResponse.idToken);
    authDispatch(setSFDCAuthTokens(tokenResponse.accessToken, tokenResponse.idToken, tokenResponse.refreshToken));
    authDispatch(setRole(tokenResponse.role ?? null));
    authDispatch(setProfileName(tokenResponse.profileName ?? null));
    authDispatch(setContactId(tokenResponse.contactId ?? null));
  } catch (error) {
    if (error instanceof TokenRefreshCancelledError) throw error;
    if (!shouldApplyResponse()) throw new TokenRefreshCancelledError();
    console.error("Error refreshing tokens", error);
    navigate(ROUTE_PATHS.LOGOUT);
    return;
  }

  return authDispatch(setInitialized());
};

// ====================================================================================================================
// useIsAuthRoute
// ====================================================================================================================

export const getIsAuthRoute = (pathname: string) => {
  const isAuthRoute = AUTH_PATHS.includes(pathname.split("?")[0] as string);
  return isAuthRoute;
};

export const useIsGetAuthRoute = () => {
  const location = useLocation();
  return getIsAuthRoute(location.pathname);
};
